import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateUserRequestSchema,
  PasswordSchema,
  type CreateUserRequest,
  USER_ROLES,
} from '@ctms/contracts';
import { z } from 'zod';
import { AlertCircle, Lock, Mail, Shield, User, UserPlus, X } from 'lucide-react';

import { usersApi } from '../users.api.js';
import { getApiErrorMessage } from '../../../api/client.js';

export interface CreateUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const CreateUserFormSchema = CreateUserRequestSchema.extend({
  password: z.union([z.literal(''), PasswordSchema]).optional(),
});

type CreateUserFormInput = z.input<typeof CreateUserFormSchema>;

export function CreateUserDialog({
  isOpen,
  onClose,
  onCreated,
}: CreateUserDialogProps): JSX.Element | null {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateUserFormInput>({
    resolver: zodResolver(CreateUserFormSchema),
    defaultValues: {
      displayName: '',
      email: '',
      role: USER_ROLES.USER,
      hasFinanceAccess: false,
      password: '',
    },
  });

  if (!isOpen) {
    return null;
  }

  const handleClose = (): void => {
    reset();
    setErrorMessage(null);
    onClose();
  };

  const onSubmit = async (data: CreateUserFormInput): Promise<void> => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const payload: CreateUserRequest = {
        displayName: data.displayName,
        email: data.email,
        role: data.role ?? USER_ROLES.USER,
        hasFinanceAccess: Boolean(data.hasFinanceAccess),
        password: data.password && data.password.trim().length > 0 ? data.password : undefined,
      };
      await usersApi.createUser(payload);
      reset();
      onCreated();
      onClose();
    } catch (err: unknown) {
      const msg = await getApiErrorMessage(
        err,
        'Nie udało się utworzyć konta pracownika. Sprawdź wprowadzone dane.',
      );
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-user-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-xs p-4"
    >
      <div className="w-full max-w-lg rounded-xl border border-slate-800 bg-slate-900 p-6 shadow-2xl text-slate-100">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-950 text-cyan-400 ring-1 ring-cyan-800">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <h2 id="create-user-title" className="text-base font-semibold text-slate-100">
                Dodaj nowego pracownika ośrodka
              </h2>
              <p className="text-xs text-slate-400">
                Utworzenie konta aplikacyjnego dla personelu
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4" noValidate>
          {errorMessage && (
            <div
              role="alert"
              className="flex items-center gap-2.5 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
            >
              <AlertCircle className="h-4 w-4 shrink-0 text-rose-400" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <label
              htmlFor="user-display-name"
              className="block text-xs font-medium uppercase tracking-wider text-slate-300"
            >
              Imię i nazwisko pracownika
            </label>
            <div className="relative mt-1.5">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
                <User className="h-4 w-4" />
              </div>
              <input
                id="user-display-name"
                type="text"
                disabled={isSubmitting}
                aria-invalid={Boolean(errors.displayName)}
                aria-describedby={errors.displayName ? 'display-name-error' : undefined}
                placeholder="np. dr Anna Kowalska"
                className="w-full rounded-md border border-slate-700 bg-slate-950 py-2 pr-3 pl-9 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 focus:outline-none disabled:opacity-50"
                {...register('displayName')}
              />
            </div>
            {errors.displayName && (
              <p id="display-name-error" className="mt-1 text-xs text-rose-400">
                {errors.displayName.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="user-email"
              className="block text-xs font-medium uppercase tracking-wider text-slate-300"
            >
              Adres e-mail (login konta)
            </label>
            <div className="relative mt-1.5">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
                <Mail className="h-4 w-4" />
              </div>
              <input
                id="user-email"
                type="email"
                disabled={isSubmitting}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'user-email-error' : undefined}
                placeholder="a.kowalska@osrodek.pl"
                className="w-full rounded-md border border-slate-700 bg-slate-950 py-2 pr-3 pl-9 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 focus:outline-none disabled:opacity-50"
                {...register('email')}
              />
            </div>
            {errors.email && (
              <p id="user-email-error" className="mt-1 text-xs text-rose-400">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="user-role"
                className="block text-xs font-medium uppercase tracking-wider text-slate-300"
              >
                Rola aplikacyjna
              </label>
              <div className="relative mt-1.5">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
                  <Shield className="h-4 w-4" />
                </div>
                <select
                  id="user-role"
                  disabled={isSubmitting}
                  className="w-full rounded-md border border-slate-700 bg-slate-950 py-2 pr-3 pl-9 text-sm text-slate-100 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 focus:outline-none disabled:opacity-50"
                  {...register('role')}
                >
                  <option value={USER_ROLES.USER}>Zwykły Użytkownik</option>
                  <option value={USER_ROLES.TENANT_ADMIN}>Administrator Ośrodka</option>
                </select>
              </div>
            </div>

            <div>
              <label
                htmlFor="user-password"
                className="block text-xs font-medium uppercase tracking-wider text-slate-300 whitespace-nowrap"
              >
                Hasło początkowe <span className="text-[10px] text-slate-400 lowercase tracking-normal font-normal">(opcjonalne)</span>
              </label>
              <div className="relative mt-1.5">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
                  <Lock className="h-4 w-4" />
                </div>
                <input
                  id="user-password"
                  type="password"
                  disabled={isSubmitting}
                  aria-invalid={Boolean(errors.password)}
                  aria-describedby={errors.password ? 'user-password-error' : undefined}
                  placeholder="Min. 8 znaków (duża, mała, cyfra)"
                  className="w-full rounded-md border border-slate-700 bg-slate-950 py-2 pr-3 pl-9 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 focus:outline-none disabled:opacity-50"
                  {...register('password')}
                />
              </div>
              {errors.password && (
                <p id="user-password-error" className="mt-1 text-xs text-rose-400">
                  {errors.password.message}
                </p>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3.5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                disabled={isSubmitting}
                className="mt-0.5 h-4 w-4 rounded border-slate-700 bg-slate-900 text-cyan-600 focus:ring-cyan-500 focus:ring-offset-slate-950"
                {...register('hasFinanceAccess')}
              />
              <div className="text-xs">
                <span className="font-medium text-slate-200">
                  Dostęp do modułu finansowego
                </span>
                <p className="mt-0.5 text-slate-400 leading-relaxed">
                  Uprawnia pracownika do wglądu i edycji budżetów badań, stawek procedur oraz
                  rozliczeń wizyt pacjentów. Domyślnie wyłączone dla Zwykłego Użytkownika.
                </p>
              </div>
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="rounded-md border border-slate-700 bg-slate-800 px-4 py-2 text-xs font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors disabled:opacity-50"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-1.5 rounded-md bg-cyan-600 px-4 py-2 text-xs font-medium text-white hover:bg-cyan-500 transition-colors focus:ring-2 focus:ring-cyan-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              <UserPlus className="h-3.5 w-3.5" />
              {isSubmitting ? 'Zapisywanie…' : 'Utwórz konto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
